import React, { useState, useEffect } from 'react';
import API from '../api/api';
import './RagDocs.css';

function RagDocs() {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [viewingDoc, setViewingDoc] = useState(null);
    const [viewContent, setViewContent] = useState('');

    useEffect(() => {
        fetchDocs();
    }, []);

    const fetchDocs = async () => {
        try {
            setLoading(true);
            const response = await API.get('/admin/rag_docs');
            setDocs(response.data);
            setError(null);
        } catch (err) {
            showError('Error al cargar documentos.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const showError = (msg) => {
        setError(msg);
        setTimeout(() => setError(null), 5000);
    };

    const showSuccess = (msg) => {
        setSuccess(msg);
        setTimeout(() => setSuccess(null), 5000);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.name.endsWith('.txt')) {
            showError('Solo se permiten archivos .txt');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        try {
            setUploading(true);
            const response = await API.post('/admin/rag_docs/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            showSuccess(response.data.message);
            fetchDocs();
        } catch (err) {
            showError(err.response?.data?.detail || 'Error al subir el archivo.');
        } finally {
            setUploading(false);
            e.target.value = null;
        }
    };

    const handleDelete = async (filename) => {
        if (!window.confirm(`¿Seguro que deseas eliminar ${filename} y desindexarlo?`)) return;

        try {
            await API.delete(`/admin/rag_docs/${filename}`);
            showSuccess(`Archivo ${filename} eliminado exitosamente.`);
            fetchDocs();
        } catch (err) {
            showError('Error al eliminar el documento.');
            console.error(err);
        }
    };

    const handleDownload = async (filename) => {
        try {
            const response = await API.get(`/admin/rag_docs/${filename}/download`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            showError('Error al descargar el archivo.');
        }
    };

    const handleView = async (filename) => {
        try {
            const response = await API.get(`/admin/rag_docs/${filename}/content`);
            setViewContent(response.data.content);
            setViewingDoc(filename);
        } catch (err) {
            showError('Error al leer el contenido.');
        }
    };

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="rag-container">
            <div className="rag-header">
                <h2>Documentos de Conocimiento (RAG)</h2>
                <label className={`upload-label ${uploading ? 'disabled' : ''}`}>
                    {uploading ? '⌛ Subiendo...' : '➕ Subir Documento (.txt)'}
                    <input
                        type="file"
                        accept=".txt"
                        style={{ display: 'none' }}
                        onChange={handleFileUpload}
                        disabled={uploading}
                    />
                </label>
            </div>

            {error && <div className="status-toast toast-error">❌ {error}</div>}
            {success && <div className="status-toast toast-success">✅ {success}</div>}

            {loading && docs.length === 0 ? (
                <div className="empty-state">Cargando documentos...</div>
            ) : docs.length === 0 ? (
                <div className="empty-state">
                    <p>No hay documentos cargados para el Chat IA.</p>
                </div>
            ) : (
                <div className="docs-grid">
                    {docs.map((doc) => (
                        <div className="doc-card" key={doc.filename}>
                            <div className="doc-icon">📄</div>
                            <div className="doc-info">
                                <span className="doc-name" title={doc.filename}>{doc.filename}</span>
                                <span className="doc-size">{formatBytes(doc.size)}</span>
                            </div>
                            <div className="doc-actions">
                                <button className="action-btn view-btn" onClick={() => handleView(doc.filename)}>👁️ Ver</button>
                                <button className="action-btn download-btn" onClick={() => handleDownload(doc.filename)}>💾 Descargar</button>
                                <button className="action-btn delete-btn" onClick={() => handleDelete(doc.filename)}>🗑️</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {viewingDoc && (
                <div className="modal-overlay" onClick={() => setViewingDoc(null)}>
                    <div className="doc-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{viewingDoc}</h3>
                            <button className="close-modal" onClick={() => setViewingDoc(null)}>&times;</button>
                        </div>
                        <div className="modal-content">
                            {viewContent}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default RagDocs;
